import { FC } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import { BuddyApi, LearnableDetailsApi } from 'src/api';
import { useUserContext } from 'src/context/user';
import { useExperienceContext } from 'src/context/experience';
import { getLanguagePairId } from 'src/utils/language/getLanguagePairId';
import { LearnableDetails } from 'src/api/learnableDetails/types';
import { BuddiesResponse } from 'src/api/buddy/types';
import { isBuddyLocked } from 'src/apps/Buddies/utils';
import { WordDetails } from 'src/apps/ScenarioGeneric/WordDetails';
import { useActivityCheck } from 'src/hooks/useActivityCheck';
import { ROUTES } from 'src/constants/routes.const';
import * as S from './styles';

interface WordDetailsModalProps {
  onClose: () => void;
  isKnown?: boolean;
  isDifficult?: boolean;
  hideDifficultToggle?: boolean;
  hideMarkAsKnownToggle?: boolean;
  onMarkAsKnown?: (learnableId: number, isKnown: boolean) => void;
  onMarkAsDifficult?: (learnableId: number, isDifficult: boolean) => void;
  targetLanguageName?: string;
  sourceLanguageName?: string;
  learnableId: number;
}

export const WordDetailsModal: FC<WordDetailsModalProps> = ({
  onClose,
  isKnown,
  isDifficult,
  hideDifficultToggle,
  hideMarkAsKnownToggle,
  onMarkAsKnown,
  onMarkAsDifficult,
  targetLanguageName,
  sourceLanguageName,
  learnableId,
}) => {
  const { asPath } = useRouter();
  const { user } = useUserContext();
  const { experienceType } = useExperienceContext();
  const languagePairId = getLanguagePairId();

  const { data: learnableDetails } = useQuery<LearnableDetails | null>({
    queryKey: ['learnableDetails', learnableId],
    queryFn: () => LearnableDetailsApi.getLearnableDetails(learnableId),
  });

  const { data } = useQuery<BuddiesResponse | null>({
    queryKey: ['buddies', languagePairId],
    queryFn: () => {
      if (!languagePairId) return null;
      return BuddyApi.getAll(languagePairId);
    },
  });

  const {
    isLoading: isLoadingActivity,
    isLocked,
    onActivityEngagement,
  } = useActivityCheck({
    enabled: !asPath.startsWith(ROUTES.aprender.learn),
    tokenType: 'pronunciation_from_my_words',
  });

  const pronunciationBuddy = data?.buddies.find(buddy => buddy.id === 'pronunciation');
  const canUsePronunciationBuddy = !!pronunciationBuddy;
  const isPronunciationBuddyLocked =
    isLocked || isBuddyLocked(user, experienceType, pronunciationBuddy, null);
  const isDataLoaded = learnableDetails && !isLoadingActivity && experienceType;

  return (
    <S.ModalWrapper isOpen handleClose={onClose} darkCloseIcon>
      {isDataLoaded && (
        <WordDetails
          experienceType={experienceType}
          learnableDetails={learnableDetails}
          targetLanguageName={targetLanguageName}
          sourceLanguageName={sourceLanguageName}
          isKnown={isKnown}
          isDifficult={isDifficult}
          hideDifficultToggle={hideDifficultToggle}
          hideMarkAsKnownToggle={hideMarkAsKnownToggle}
          canUsePronunciationBuddy={canUsePronunciationBuddy}
          isPronunciationBuddyLocked={isPronunciationBuddyLocked}
          onMarkAsKnown={onMarkAsKnown}
          onMarkAsDifficult={onMarkAsDifficult}
          onActivityEngagement={onActivityEngagement}
        />
      )}
    </S.ModalWrapper>
  );
};

export default WordDetailsModal;
